import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { LifeDomain, PersonalModelResponse } from "../shared/living-brain";
import {
  LIVING_SELF_MODEL_AUTHORITY_LABELS,
  type LivingSelfModelAuthorityResponse,
} from "../shared/living-self-model-bedrock";
import { api } from "./lib/api";

const DOMAIN_LABELS: Record<LifeDomain, string> = {
  self: "Self",
  body: "Body",
  love: "Love",
  money: "Money",
  work: "Work",
};

const DOMAIN_ORDER: LifeDomain[] = ["self", "body", "love", "money", "work"];

function SourceTrail({
  sourceIds,
  relatedIds = [],
}: {
  sourceIds: string[];
  relatedIds?: string[];
}) {
  if (sourceIds.length === 0 && relatedIds.length === 0) return null;
  return <details className="model-source-trail">
    <summary>Why am I seeing this?</summary>
    {sourceIds.length > 0 && <>
      <p className="fine">These are the canonical source records the current model points to.</p>
      <ul>
        {sourceIds.map(id => <li key={id}><code>{id}</code></li>)}
      </ul>
    </>}
    {relatedIds.length > 0 && <>
      <p className="fine">This open question refers to these current model items.</p>
      <ul>
        {relatedIds.map(id => <li key={id}><code>{id}</code></li>)}
      </ul>
    </>}
  </details>;
}

function AuthorityControls({
  hypothesisId,
  onSaved,
}: {
  hypothesisId: string;
  onSaved: (message: string) => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<LivingSelfModelAuthorityResponse | null>(null);
  const [error, setError] = useState("");

  async function respond(response: LivingSelfModelAuthorityResponse) {
    const requiresText = ["partly", "not_what_i_meant", "missing_context"].includes(response);
    if (requiresText && !text.trim()) {
      setError(
        response === "missing_context"
          ? "Tell The Mirror what context is missing."
          : "Give The Mirror the wording that fits more accurately.",
      );
      return;
    }
    setBusy(response);
    setError("");
    try {
      const result = await api<{
        response: LivingSelfModelAuthorityResponse;
        currentTruthTreatment: string;
        successorHypothesisId?: string;
        authority_note: string;
      }>(`/api/hypotheses/${encodeURIComponent(hypothesisId)}/authority`, {
        method: "POST",
        body: JSON.stringify({
          response,
          ...(text.trim() ? { text: text.trim() } : {}),
        }),
      });
      setText("");
      onSaved(
        result.successorHypothesisId
          ? "Saved. Your wording created a revised working hypothesis; the earlier version remains historical."
          : result.currentTruthTreatment === "retired"
            ? "Saved. That view is no longer treated as current; its historical Episodes remain unchanged."
            : result.currentTruthTreatment === "context_missing"
              ? "Saved. The Mirror will treat this as incomplete context rather than settled truth."
              : "Saved. Your authority is now part of the canonical record.",
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save your response");
    } finally {
      setBusy(null);
    }
  }

  return <div className="model-authority" data-testid={`hypothesis-authority-${hypothesisId}`}>
    <p><strong>Does this fit?</strong></p>
    <textarea
      aria-label="Your wording or missing context"
      value={text}
      onChange={event => setText(event.target.value)}
      placeholder="Optional for Yes, No, or That used to be true. Required when narrowing, correcting, or adding missing context."
    />
    <div className="actions">
      {(Object.keys(LIVING_SELF_MODEL_AUTHORITY_LABELS) as LivingSelfModelAuthorityResponse[]).map(response =>
        <button
          key={response}
          className={response === "yes" ? undefined : "ghost"}
          disabled={busy !== null}
          data-authority-response={response}
          onClick={() => void respond(response)}
        >
          {busy === response ? "Saving…" : LIVING_SELF_MODEL_AUTHORITY_LABELS[response]}
        </button>,
      )}
    </div>
    {error && <p role="alert">{error}</p>}
  </div>;
}

function ModelPage({ children }: { children: ReactNode }) {
  return <main className="flow model-view">
    <a className="wordmark" href="/today">WORD &amp; WILL</a>
    <section>
      <p className="eyebrow">THE MIRROR</p>
      {children}
      <nav className="actions" aria-label="Private Mirror navigation">
        <a href="/today">Today</a>
        <a href="/catch">Catch</a>
        <a href="/model">Current view</a>
        <a href="/evidence">Evidence</a>
        <a href="/privacy-settings">Privacy</a>
        <a href="/voice">Mirror Voice</a>
      </nav>
    </section>
  </main>;
}

export function PersonalModelSurface() {
  const [model, setModel] = useState<PersonalModelResponse | null>(null);
  const [error, setError] = useState("");
  const [refreshMessage, setRefreshMessage] = useState("");
  const [refreshBusy, setRefreshBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      setModel(await api<PersonalModelResponse>("/api/v2/brain/model"));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not load the current Mirror view");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const domains = useMemo(
    () => model
      ? DOMAIN_ORDER.map(domain => ({ domain, ...model.domains[domain] }))
      : [],
    [model],
  );

  async function requestRefresh() {
    setRefreshBusy(true);
    setRefreshMessage("");
    setError("");
    try {
      const result = await api<{
        job_id: string;
        state: string;
        queued: boolean;
        note: string;
      }>("/api/v2/brain/model/refresh", {
        method: "POST",
        body: "{}",
      });
      setRefreshMessage(
        [result.note, " Refresh state: ", result.state, result.queued ? " (queued)." : "."].join(""),
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not request a model refresh");
    } finally {
      setRefreshBusy(false);
    }
  }

  if (error && !model) {
    return <ModelPage>
      <h1>What The Mirror currently sees</h1>
      <p role="alert">{error}</p>
      <a className="button secondary" href="/today">Back to Today</a>
    </ModelPage>;
  }

  if (!model) {
    return <ModelPage>
      <h1>What The Mirror currently sees</h1>
      <p>Reading your current private record…</p>
    </ModelPage>;
  }

  return <ModelPage>
    <header className="model-header">
      <div>
        <h1 data-testid="personal-model-title">What The Mirror currently sees</h1>
        <p className="lede" data-testid="personal-model-note">{model.note}</p>
      </div>
      <div>
        <button
          data-testid="personal-model-refresh"
          className="ghost"
          disabled={refreshBusy}
          onClick={() => void requestRefresh()}
        >
          {refreshBusy ? "Requesting…" : "Refresh this view"}
        </button>
        <p className="fine">
          Version {model.version} · generated {new Date(model.generated_at).toLocaleString()}
        </p>
      </div>
    </header>

    {refreshMessage && <p role="status" data-testid="personal-model-refresh-status">{refreshMessage}</p>}
    {error && <p role="alert">{error}</p>}

    <section className="model-section" data-testid="personal-model-wills">
      <p className="eyebrow">WHAT YOU WANT</p>
      <h2>Your active Wills</h2>
      {model.active_wills.length === 0
        ? <p>No active Will is in this current view.</p>
        : <div className="model-grid">{model.active_wills.map(will =>
          <article className="model-card" key={will.id}>
            <h3>{will.statement}</h3>
            {will.domains.length > 0 && <p className="fine">
              {will.domains.map(domain => DOMAIN_LABELS[domain]).join(" · ")}
            </p>}
            <SourceTrail sourceIds={will.source_ids}/>
          </article>,
        )}</div>}
    </section>

    <section className="model-section" data-testid="personal-model-domains">
      <p className="eyebrow">THE AREAS IN VIEW</p>
      <h2>Where your recent record is pointing</h2>
      <div className="model-grid model-domain-grid">
        {domains.map(item => <article className="model-card" key={item.domain} data-domain={item.domain}>
          <h3>{DOMAIN_LABELS[item.domain]}</h3>
          {item.summary
            ? <p>{item.summary}</p>
            : <p className="fine">No current summary has enough recorded material to show here.</p>}
          {item.recent_episode_count > 0 && <p className="fine">
            {item.recent_episode_count} recent Episode{item.recent_episode_count === 1 ? "" : "s"}
          </p>}
          <SourceTrail sourceIds={item.source_ids}/>
        </article>)}
      </div>
    </section>

    {model.cross_domain_connections.length > 0 && <section className="model-section" data-testid="personal-model-cross-domain">
      <p className="eyebrow">POSSIBLE CONNECTIONS</p>
      <h2>Something worth checking across areas of your life</h2>
      <p>The Mirror only shows these when your private settings allow cross-domain learning and the canonical record meets the stronger cross-domain evidence threshold.</p>
      {model.cross_domain_connections.map(connection => <article className="model-card" key={connection.hypothesis_id}>
        <p className="eyebrow">POSSIBLE CONNECTION · {connection.domains.map(domain => DOMAIN_LABELS[domain]).join(" · ")}</p>
        <h3>{connection.observation}</h3>
        <p>{connection.working_hypothesis}</p>
        <p className="fine">{connection.support_count} independent supporting Episodes across these domains{connection.conflict_count ? ` · ${connection.conflict_count} conflicting record(s) remain visible` : ""}.</p>
        <SourceTrail sourceIds={connection.source_ids}/>
        <AuthorityControls hypothesisId={connection.hypothesis_id} onSaved={setRefreshMessage}/>
      </article>)}
    </section>}

    <section className="model-section" data-testid="personal-model-hypotheses">
      <p className="eyebrow">WHAT KEEPS APPEARING</p>
      <h2>Working observations worth checking</h2>
      <p>The Mirror can notice repetition. It does not get to turn repetition into who you are.</p>
      {model.hypotheses.length === 0
        ? <p className="fine">No current hypothesis is strong enough to put in front of you.</p>
        : model.hypotheses.map(hypothesis => <article className="model-card" key={hypothesis.id}>
          <p className="eyebrow">WORKING HYPOTHESIS · {hypothesis.status.replaceAll("_", " ")}</p>
          <h3>{hypothesis.text}</h3>
          <p className="fine">
            Evidence in the current model: {hypothesis.support_count} supporting · {hypothesis.conflict_count} conflicting · {hypothesis.neutral_count} neutral.
          </p>
          <p>You remain the authority on what this means. The source trail stays visible even when you revise or retire the current view.</p>
          <SourceTrail sourceIds={hypothesis.source_ids}/>
          <AuthorityControls hypothesisId={hypothesis.id} onSaved={setRefreshMessage}/>
        </article>)}
    </section>

    <section className="model-section" data-testid="personal-model-open-loops">
      <p className="eyebrow">WHAT'S STILL OPEN</p>
      <h2>Things reality has not answered yet</h2>
      {model.open_loops.length === 0
        ? <p className="fine">No open Prediction, Move, or evidence check-in is in this view.</p>
        : model.open_loops.map(loop => <article className="model-card" key={loop.type + ":" + loop.id}>
          <p className="eyebrow">{loop.type.replaceAll("_", " ").toUpperCase()}</p>
          <h3>{loop.headline}</h3>
          {loop.due_at && <p className="fine">Due {new Date(loop.due_at).toLocaleString()}</p>}
          <SourceTrail sourceIds={loop.source_ids}/>
        </article>)}
    </section>

    <section className="model-section" data-testid="personal-model-unknowns">
      <p className="eyebrow">WHAT THE MIRROR DOESN'T KNOW YET</p>
      <h2>Open questions</h2>
      {model.known_unknowns.length === 0
        ? <p className="fine">There is no explicit known unknown in this current view.</p>
        : model.known_unknowns.map((unknown, index) => <article className="model-card" key={unknown.question + ":" + index}>
          <h3>{unknown.question}</h3>
          <p>{unknown.reason}</p>
          <SourceTrail sourceIds={[]} relatedIds={unknown.related_ids}/>
        </article>)}
    </section>

    <aside>
      <p className="eyebrow">YOUR AUTHORITY</p>
      <h2>The Mirror can be wrong.</h2>
      <p>If something here is incomplete, outdated, or not what you meant, your response is saved as new canonical authority. The earlier record remains inspectable rather than being silently rewritten.</p>
    </aside>
  </ModelPage>;
}
